/**
 * @fileoverview Hook for managing drag & drop logic for cost centers
 * Provides state management and operations for ordering production processes
 */

import { useState, useCallback, useMemo } from 'react';
import { CentroCosto } from '../types/collections/CentroCosto';
import { CentroCostoPeso } from '../types/collections/CentroCostoPeso';

/**
 * Hook for managing drag & drop operations for cost centers
 * @param initialCentrosCosto - Initial list of available cost centers
 * @returns Object with state and functions for drag & drop operations
 */
export const useDragDrop = (initialCentrosCosto: CentroCosto[] = []) => {
  // State management - now using CentroCostoPeso structure
  const [secuencia, setSecuencia] = useState<CentroCostoPeso[]>([]);
  const [centrosDisponibles, setCentrosDisponibles] = useState<string[]>(
    initialCentrosCosto.map(centro => centro.id)
  );

  /**
   * Add a cost center to the sequence
   * @param centroCostoId - ID of the cost center to add
   * @param peso - Weight percentage (default 0)
   */
  const agregarACSecuencia = useCallback((centroCostoId: string, peso: number = 0) => {
    const nuevoCentroPeso: CentroCostoPeso = {
      id: `temp_${Date.now()}`, // Temporary ID for new items
      centro_costo: centroCostoId,
      peso: peso,
      expand: {
        centro_costo: initialCentrosCosto.find(c => c.id === centroCostoId) as any
      }
    } as CentroCostoPeso;
    
    setSecuencia(prev => [...prev, nuevoCentroPeso]);
    setCentrosDisponibles(prev => prev.filter(id => id !== centroCostoId));
  }, [initialCentrosCosto]);

  /**
   * Remove a cost center from the sequence
   * @param index - Index of the cost center to remove
   */
  const removerDeSecuencia = useCallback((index: number) => {
    setSecuencia(prev => {
      const nuevaSecuencia = [...prev];
      const removido = nuevaSecuencia.splice(index, 1)[0];
      
      // Add back to available centers using the centro_costo ID
      if (removido.centro_costo) {
        setCentrosDisponibles(prevDisponibles => [...prevDisponibles, removido.centro_costo!]);
      }
      
      return nuevaSecuencia;
    });
  }, []);

  /**
   * Reorder elements in the sequence
   * @param fromIndex - Source index
   * @param toIndex - Destination index
   */
  const reordenarSecuencia = useCallback((fromIndex: number, toIndex: number) => {
    setSecuencia(prev => {
      const nuevaSecuencia = [...prev];
      const [removido] = nuevaSecuencia.splice(fromIndex, 1);
      nuevaSecuencia.splice(toIndex, 0, removido);
      return nuevaSecuencia;
    });
  }, []);

  /**
   * Duplicate a cost center in the sequence
   * @param centroCostoId - ID of the cost center to duplicate
   * @param peso - Weight percentage (default 0)
   */
  const duplicarEnSecuencia = useCallback((centroCostoId: string, peso: number = 0) => {
    const nuevoCentroPeso: CentroCostoPeso = {
      id: `temp_${Date.now()}`, // Temporary ID for new items
      centro_costo: centroCostoId,
      peso: peso,
      expand: {
        centro_costo: initialCentrosCosto.find(c => c.id === centroCostoId) as any
      }
    } as CentroCostoPeso;
    
    setSecuencia(prev => [...prev, nuevoCentroPeso]);
  }, [initialCentrosCosto]);

  /**
   * Move a cost center from sequence back to available
   * @param index - Index of the cost center to move back
   */
  const moverAcentrosDisponibles = useCallback((index: number) => {
    setSecuencia(prev => {
      const nuevaSecuencia = [...prev];
      const movido = nuevaSecuencia.splice(index, 1)[0];
      
      // Add back to available centers using the centro_costo ID
      if (movido.centro_costo) {
        setCentrosDisponibles(prevDisponibles => [...prevDisponibles, movido.centro_costo!]);
      }
      
      return nuevaSecuencia;
    });
  }, []);

  /**
   * Clear the entire sequence
   */
  const limpiarSecuencia = useCallback(() => {
    setSecuencia([]);
    setCentrosDisponibles(initialCentrosCosto.map(centro => centro.id));
  }, [initialCentrosCosto]);

  /**
   * Set a new sequence (useful for editing existing plans)
   * @param nuevaSecuencia - New sequence to set (can be CentroCostoPeso[] or string[])
   */
  const establecerSecuencia = useCallback((nuevaSecuencia: CentroCostoPeso[] | string[]) => {

    
    // Handle both old format (string[]) and new format (CentroCostoPeso[])
    if (!nuevaSecuencia || nuevaSecuencia.length === 0) {
      // Empty sequence
      setSecuencia([]);
      setCentrosDisponibles(initialCentrosCosto.map(centro => centro.id));
      return;
    }

    if (typeof nuevaSecuencia[0] === 'string') {

      // Convert old format (string[]) to new format
      const secuenciaNueva: CentroCostoPeso[] = (nuevaSecuencia as string[]).map(id => {
        const centroCosto = initialCentrosCosto.find(c => c.id === id);

        
        return {
          id: `temp_${Date.now()}_${id}`,
          centro_costo: id,
          peso: 0,
          expand: {
            centro_costo: centroCosto as any
          }
        } as CentroCostoPeso;
      });
      

      setSecuencia(secuenciaNueva as CentroCostoPeso[]);
      
      // Update available centers by removing used ones
      const centrosUsados = new Set(nuevaSecuencia as string[]);
      setCentrosDisponibles(
        initialCentrosCosto
          .map(centro => centro.id)
          .filter(id => !centrosUsados.has(id))
      );
    } else {

      // New format (CentroCostoPeso[])
      // Ensure each item has expanded data
      const secuenciaNueva = (nuevaSecuencia as CentroCostoPeso[])
        .filter(item => item != null) // Filter out null/undefined items first
        .map(item => {
        // Determinar si estamos trabajando con un centro_costo_peso o un centro_costo
        // Si el ID tiene el formato de un centro_costo_peso (como 13xt3vp271c9n6v), es un centro_costo_peso
        // y debemos obtener su centro_costo relacionado
        

        
        // Si no tiene centro_costo, no podemos hacer mucho
        if (!item.centro_costo) {

          return item;
        }
        
        // Intentamos determinar si este es un centro_costo_peso o un centro_costo
        // Si ya tiene expand.centro_costo, usamos eso
        if (item.expand && item.expand.centro_costo) {

          return item;
        }
        
        // Si no tiene expand o no tiene centro_costo expandido, intentamos buscarlo
        // Primero intentamos buscar directamente el centro_costo
        let centroCosto = initialCentrosCosto.find(c => c.id === item.centro_costo);
        
        // Si no lo encontramos, puede ser porque item.centro_costo es un ID de centro_costo_peso
        // En ese caso, necesitaríamos obtener el centro_costo relacionado desde la API
        if (!centroCosto) {
          // Aquí deberíamos hacer una llamada a la API para obtener el centro_costo_peso y su centro_costo relacionado
          // Pero como no podemos hacer eso aquí, devolvemos el item sin cambios
          return item;
        } else {
          return {
            ...item,
                      expand: {
            centro_costo: centroCosto as any
          }
          };
        }
      });
      

      setSecuencia(secuenciaNueva as CentroCostoPeso[]);
      
      // Update available centers by removing used ones
      const centrosUsados = new Set(secuenciaNueva.filter(item => item != null).map(item => item.centro_costo).filter(Boolean));
      setCentrosDisponibles(
        initialCentrosCosto
          .map(centro => centro.id)
          .filter(id => !centrosUsados.has(id))
      );
    }
  }, [initialCentrosCosto]);

  /**
   * Get the current sequence with full cost center objects
   * @param centrosCosto - Full list of cost centers for reference
   * @returns Array of CentroCostoPeso objects in sequence order
   */
  const obtenerSecuenciaCompleta = useCallback((centrosCosto: CentroCosto[]) => {
    return secuencia.map(item => {
      // If the item doesn't have expanded data, try to find it
      if (!item.expand?.centro_costo && item.centro_costo) {
        const centro = centrosCosto.find(c => c.id === item.centro_costo);
        return {
          ...item,
          expand: {
            centro_costo: centro
          }
        };
      }
      return item;
    }).filter(Boolean);
  }, [secuencia]);

  /**
   * Check if a cost center is available for selection
   * @param centroCostoId - ID of the cost center to check
   * @returns True if the cost center is available
   */
  const estaDisponible = useCallback((centroCostoId: string) => {
    return centrosDisponibles.includes(centroCostoId);
  }, [centrosDisponibles]);

  /**
   * Get the count of available cost centers
   * @returns Number of available cost centers
   */
  const centrosDisponiblesCount = useMemo(() => centrosDisponibles.length, [centrosDisponibles]);

  /**
   * Get the count of cost centers in sequence
   * @returns Number of cost centers in sequence
   */
  const secuenciaCount = useMemo(() => secuencia.length, [secuencia]);

  /**
   * Check if the sequence is empty
   * @returns True if the sequence is empty
   */
  const secuenciaVacia = useMemo(() => secuencia.length === 0, [secuencia]);

  /**
   * Check if there are no available cost centers
   * @returns True if no cost centers are available
   */
  const sinCentrosDisponibles = useMemo(() => centrosDisponibles.length === 0, [centrosDisponibles]);

  /**
   * Validate the current sequence
   * @returns Object with validation result and any errors
   */
  const validarSecuencia = useCallback(() => {
    const errors: string[] = [];
    const errorFields: { index: number; field: string; message: string }[] = [];

    if (secuencia.length === 0) {
      errors.push("La secuencia debe contener al menos un centro de costo");
    }

    if (secuencia.length > 999) {
      errors.push("La secuencia no puede exceder 999 centros de costo");
    }

    // Check for duplicate consecutive items (if that's a business rule)
    const duplicados = secuencia.filter((item, index) => 
      secuencia.findIndex(s => s.centro_costo === item.centro_costo) !== index
    );
    
    if (duplicados.length > 0) {
      errors.push("La secuencia contiene elementos duplicados");
    }

    // Validate weights
    const pesos = secuencia.map(item => item.peso || 0);
    const totalPeso = pesos.reduce((sum, peso) => sum + peso, 0);
    
    // Verificar que la suma sea exactamente 100%
    if (totalPeso !== 100) {
      errors.push(`La suma total de pesos debe ser exactamente 100%. Actualmente es: ${totalPeso}%`);
    }

    // Check individual weights - deben ser mayores a 0 y máximo 100
    for (let i = 0; i < pesos.length; i++) {
      const peso = pesos[i];
      if (peso <= 0) {
        errors.push("Todos los procesos deben tener un peso mayor a 0");
        errorFields.push({
          index: i,
          field: 'peso',
          message: 'El peso debe ser mayor a 0'
        });
        break;
      }
      
      if (peso > 100) {
        errors.push("Los porcentajes de peso no pueden exceder 100");
        errorFields.push({
          index: i,
          field: 'peso',
          message: 'El peso no puede exceder 100%'
        });
        break;
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      totalPeso,
      errorFields,
      firstErrorIndex: errorFields.length > 0 ? errorFields[0].index : -1
    };
  }, [secuencia]);

  /**
   * Export the current sequence for saving
   * @returns Current sequence array (CentroCostoPeso[])
   */
  const exportarSecuencia = useCallback(() => {
    return [...secuencia];
  }, [secuencia]);

  /**
   * Import a sequence from external source
   * @param nuevaSecuencia - Sequence to import (CentroCostoPeso[] or string[])
   */
  const importarSecuencia = useCallback((nuevaSecuencia: CentroCostoPeso[] | string[]) => {
    establecerSecuencia(nuevaSecuencia);
  }, [establecerSecuencia]);

  /**
   * Update the weight of a cost center in the sequence
   * @param index - Index of the cost center to update
   * @param peso - New weight value
   */
  const actualizarPeso = useCallback((index: number, peso: number) => {
    setSecuencia(prev => {
      const nuevaSecuencia = [...prev];
      if (nuevaSecuencia[index]) {
        nuevaSecuencia[index] = {
          ...nuevaSecuencia[index],
          peso: Math.max(0, Math.min(100, peso)) // Clamp between 0 and 100
        };
      }
      return nuevaSecuencia;
    });
  }, []);

  return {
    // State
    secuencia,
    centrosDisponibles,
    
    // Basic operations
    agregarACSecuencia,
    removerDeSecuencia,
    reordenarSecuencia,
    duplicarEnSecuencia,
    moverAcentrosDisponibles,
    
    // Sequence management
    limpiarSecuencia,
    establecerSecuencia,
    obtenerSecuenciaCompleta,
    
    // Weight management
    actualizarPeso,
    
    // Utility functions
    estaDisponible,
    validarSecuencia,
    exportarSecuencia,
    importarSecuencia,
    
    // Computed values
    centrosDisponiblesCount,
    secuenciaCount,
    secuenciaVacia,
    sinCentrosDisponibles
  };
};
